import clsx from 'clsx'
import {isUndefined, omit} from 'lodash'
import {ReactElement, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import A1_CONSTANTS from '../../core/_const'
import {InputFilter} from '../../../../../_gori/partials/widgets/form/InputFilter'
import {InputSearch, SelectMultiDropdown} from '../../../../../_gori/partials/widgets'
import {DEFAULT_PAGE, OPTION_COUNTRIES} from '../../../../../_gori/constants'
import {KTSVG} from '../../../../../_gori/helpers'
import {DatePicker} from '../../../../../_gori/helpers/components/DatePicker'
import {SaveFilterButton} from '../../../orders'
import {useOrderFilter} from '../../../orders/hooks'

type A1BatchesDetailTableFilterProps = {
  rightComponent?: ReactElement
  className?: string
}
const A1BatchesDetailTableFilter = ({
  rightComponent,
  className = '',
}: A1BatchesDetailTableFilterProps) => {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [clickFilter, setClickFilter] = useState<boolean>(false)

  const {optionsDomesticService, optionPackageInfo} = useOrderFilter()

  const [showModal, setShowModal] = useState<{
    save_current_filter: boolean
  }>({
    save_current_filter: false,
  })

  const initFilterOrder = useMemo(() => {
    return A1_CONSTANTS.PARAMS_A1_BATCHES_DETAIL_TABLE_FILTER.reduce((acc, param) => {
      acc[param] = searchParams.get(param) || undefined
      return acc
    }, {})
  }, [searchParams])

  const hasFilter = useMemo(() => {
    return Object.values(omit(initFilterOrder, 'search_text')).some(
      (filter) => !isUndefined(filter)
    )
  }, [initFilterOrder])

  const [dataFilters, setDataFilters] = useState<{[key: string]: any}>(initFilterOrder)

  useEffect(() => {
    setDataFilters(initFilterOrder)
  }, [initFilterOrder])

  const handleFiltersOrder = (key: string, value: any) => {
    value = value.toString()
    value.trim() ? searchParams.set(key, value.trim()) : searchParams.delete(key)
    searchParams.set('page', DEFAULT_PAGE)
    setSearchParams(searchParams)
  }

  const handleClearAll = () => {
    A1_CONSTANTS.PARAMS_A1_BATCHES_DETAIL_TABLE_FILTER.forEach((param) =>
      searchParams.delete(param)
    )
    setSearchParams(searchParams)
    setClickFilter(false)
  }

  return (
    <div className={clsx('p-8 pb-0', className)}>
      <div className='d-flex flex-wrap flex-fill justify-content-between align-items-start'>
        <div className='d-flex align-items-start position-relative my-1'>
          <InputSearch
            className='min-w-350px'
            placeholder={intl.formatMessage({id: 'SEARCH_TRACKING_HAWB_HTS_CODE'})}
          />
        </div>
        <div className='d-flex flex-wrap align-items-start'>
          <DatePicker
            dateFrom='date_from'
            dateTo='date_to'
            name={intl.formatMessage({id: 'ORDER_DATE'})}
            className='m-2 btn-light'
          />
          <div className='d-flex justify-content-center align-items-center'>
            <div
              className={clsx('btn btn-icon border border-light btn-active-light-primary m-2', {
                'border border-primary rounded-2 active': clickFilter,
                'bg-primary text-hover-primary': hasFilter && !clickFilter,
              })}
              onClick={() => setClickFilter(!clickFilter)}
            >
              <KTSVG
                path='/media/gori/orders/filter.svg'
                className={clsx('svg-icon svg-icon-1', {
                  'text-white': hasFilter && !clickFilter,
                })}
                small={false}
              />
            </div>
          </div>
          <SaveFilterButton
            setClickFilter={setClickFilter}
            showModalSaveCurrentFilter={showModal.save_current_filter}
            setShowModalSaveCurrentFilter={setShowModal}
            dataFilters={dataFilters}
            handleClearAll={handleClearAll}
            className='m-2'
            filterKey='a1_order_filter'
            paramsOptions={A1_CONSTANTS.PARAMS_A1_BATCHES_DETAIL_TABLE_FILTER}
          />
          {rightComponent || null}
        </div>
      </div>
      {clickFilter && (
        <div className='d-flex flex-wrap justify-content-end'>
          <span
            className='text-gray-600 text-hover-danger m-2 d-flex align-items-center cursor-pointer'
            onClick={handleClearAll}
          >
            <span className='fw-bolder me-2'>{intl.formatMessage({id: 'CLEAR_ALL'})}</span> X
          </span>
          {hasFilter && (
            <span
              className='text-primary bg-hover-light px-2 rounded-2 m-2 d-flex align-items-center cursor-pointer'
              onClick={() => setShowModal((prev) => ({...prev, save_current_filter: true}))}
            >
              <span className='fw-bolder me-2'>
                {intl.formatMessage({id: 'SAVE_CURRENT_FILTER'})}
              </span>
            </span>
          )}
          <InputFilter
            className='m-2 bg-input-dark rounded-2'
            name={intl.formatMessage({id: 'CONTAINER_ID'})}
            valueInput={dataFilters.container_id}
            setValueInput={(value) => {
              handleFiltersOrder('container_id', value)
            }}
          />
          <InputFilter
            className='m-2 bg-input-dark rounded-2'
            name={intl.formatMessage({id: 'CONTENT'})}
            valueInput={dataFilters.content}
            setValueInput={(value) => {
              handleFiltersOrder('content', value)
            }}
          />
          <InputFilter
            className='m-2 bg-input-dark rounded-2'
            name={intl.formatMessage({id: 'HTS_CODE'})}
            valueInput={dataFilters.hts_code}
            setValueInput={(value) => {
              handleFiltersOrder('hts_code', value)
            }}
          />
          <SelectMultiDropdown
            className='m-2'
            labelClassName='btn-light'
            name={intl.formatMessage({id: 'COUNTRY_OF_ORIGIN'})}
            dataOptions={OPTION_COUNTRIES}
            valueOptions={dataFilters.country_of_origin}
            handleSetValueOptions={(value) => {
              handleFiltersOrder('country_of_origin', value)
            }}
            hasClearValue
            isSearch
            placeholder={intl.formatMessage({id: 'SEARCH'})}
          />
          <SelectMultiDropdown
            className='m-2'
            labelClassName='btn-light'
            name={intl.formatMessage({id: 'CUSTOM_STATUS'})}
            hasUseIntl
            dataOptions={Object.values(A1_CONSTANTS.A1_ORDERS_OPTION_RELEASE_STATUS)}
            valueOptions={dataFilters.release_status}
            handleSetValueOptions={(value) => {
              handleFiltersOrder('release_status', value)
            }}
            hasClearValue
          />
          <InputFilter
            className='m-2 bg-input-dark'
            name={intl.formatMessage({id: 'CUSTOMER'})}
            valueInput={dataFilters.customer}
            setValueInput={(value) => {
              handleFiltersOrder('customer', value)
            }}
          />
          <InputFilter
            className='m-2 bg-input-dark'
            name={intl.formatMessage({id: 'ORDER_REF'})}
            valueInput={dataFilters.order_ref}
            setValueInput={(value) => {
              handleFiltersOrder('order_ref', value)
            }}
          />
          <InputFilter
            className='m-2 bg-input-dark'
            name={intl.formatMessage({id: 'ITEM_SKU'})}
            valueInput={dataFilters.item_sku}
            setValueInput={(value) => {
              handleFiltersOrder('item_sku', value)
            }}
          />
          <InputFilter
            className='m-2 bg-input-dark'
            name={intl.formatMessage({id: 'ITEM_NAME'})}
            valueInput={dataFilters.item_name}
            setValueInput={(value) => {
              handleFiltersOrder('item_name', value)
            }}
          />
          <SelectMultiDropdown
            className='m-2 bg-input-dark'
            name={intl.formatMessage({id: 'SERVICE'})}
            dataOptions={optionsDomesticService}
            valueOptions={dataFilters.service}
            handleSetValueOptions={(service) => handleFiltersOrder('service', service)}
            placeholder={intl.formatMessage({id: 'SEARCH'})}
            isSearch
            hasClearValue
            firstOption
          />
          <SelectMultiDropdown
            className='m-2 bg-input-dark'
            name={intl.formatMessage({id: 'PACKAGE_INFO'})}
            dataOptions={optionPackageInfo}
            valueOptions={dataFilters.package_info}
            handleSetValueOptions={(value) => handleFiltersOrder('package_info', value)}
            hasClearValue
          />
        </div>
      )}
    </div>
  )
}

export {A1BatchesDetailTableFilter}
